import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Badge, Alert, Modal, ListGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

function ProcessBuilder() {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  // State
  const [loading, setLoading] = useState(true);
  const [processes, setProcesses] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [view, setView] = useState('list'); // 'list', 'select', 'chat', 'complete', 'detail'

  // Chat state
  const [processId, setProcessId] = useState(null);
  const [processType, setProcessType] = useState('checklist');
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [sending, setSending] = useState(false);
  const [readyToFinalize, setReadyToFinalize] = useState(false);
  const [processName, setProcessName] = useState('');

  // Finalized process
  const [completedProcess, setCompletedProcess] = useState(null);

  // Detail view
  const [detailProcess, setDetailProcess] = useState(null);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadData = async () => {
    try {
      // Load processes
      const processRes = await fetch('/api/authority-engineer/process/list', {
        credentials: 'include'
      });
      if (processRes.ok) {
        const data = await processRes.json();
        if (data.success) {
          setProcesses(data.processes || []);
        }
      }

      // Load frameworks for "Build from IP" option
      const frameworkRes = await fetch('/api/authority-engineer/extract/frameworks', {
        credentials: 'include'
      });
      if (frameworkRes.ok) {
        const data = await frameworkRes.json();
        if (data.success) {
          setFrameworks(data.frameworks.filter(f => f.status !== 'extracting') || []);
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const startNewProcess = () => {
    setView('select');
  };

  const handleStartBuilder = async () => {
    setSending(true);
    try {
      const res = await fetch('/api/authority-engineer/process/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          process_type: processType,
          framework_id: selectedFramework?.id || null
        })
      });

      const data = await res.json();

      if (data.success) {
        setProcessId(data.process_id);
        setMessages(data.messages || []);
        setView('chat');
      } else {
        alert(data.error || 'Failed to start process builder');
      }
    } catch (err) {
      console.error('Error starting builder:', err);
      alert('Error starting process builder');
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!userInput.trim() || sending) return;

    const message = userInput.trim();
    setUserInput('');
    setSending(true);

    // Optimistically add user message
    setMessages(prev => [...prev, { role: 'user', content: message }]);

    try {
      const res = await fetch('/api/authority-engineer/process/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          process_id: processId,
          message: message
        })
      });

      const data = await res.json();

      if (data.success) {
        setMessages(data.messages);
        setReadyToFinalize(data.ready_to_finalize);
      } else {
        alert(data.error || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Error sending message');
    } finally {
      setSending(false);
    }
  };

  const handleFinalize = async () => {
    if (!processName.trim()) {
      alert('Please enter a name for your process');
      return;
    }

    setSending(true);
    try {
      const res = await fetch('/api/authority-engineer/process/finalize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          process_id: processId,
          name: processName.trim()
        })
      });

      const data = await res.json();

      if (data.success) {
        setCompletedProcess(data.process);
        setView('complete');
        loadData(); // Refresh list
      } else {
        alert(data.error || 'Failed to finalize process');
      }
    } catch (err) {
      console.error('Error finalizing:', err);
      alert('Error finalizing process');
    } finally {
      setSending(false);
    }
  };

  const handleAbandon = async () => {
    if (!window.confirm('Are you sure you want to abandon this process? All progress will be lost.')) {
      return;
    }

    try {
      await fetch('/api/authority-engineer/process/abandon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ process_id: processId })
      });
    } catch (err) {
      console.error('Error abandoning:', err);
    }

    setProcessId(null);
    setMessages([]);
    setReadyToFinalize(false);
    setProcessName('');
    setView('list');
    loadData();
  };

  const viewProcessDetail = async (id) => {
    try {
      const res = await fetch(`/api/authority-engineer/process/${id}`, {
        credentials: 'include'
      });
      const data = await res.json();

      if (data.success) {
        setDetailProcess(data.process);
        setView('detail');
      }
    } catch (err) {
      console.error('Error loading process:', err);
    }
  };

  const handleExport = async (id) => {
    try {
      const res = await fetch(`/api/authority-engineer/process/${id}/export`, {
        credentials: 'include'
      });
      const data = await res.json();

      if (data.success) {
        // Download as markdown file
        const blob = new Blob([data.markdown], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Error exporting:', err);
      alert('Error exporting process');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const res = await fetch(`/api/authority-engineer/process/${deleteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setProcesses(prev => prev.filter(p => p.id !== deleteId));
        if (detailProcess?.id === deleteId) {
          setView('list');
          setDetailProcess(null);
        }
      }
    } catch (err) {
      console.error('Error deleting:', err);
    }

    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const getProcessTypeBadge = (type) => {
    const variants = {
      'checklist': { bg: 'primary', label: 'Checklist' },
      'decision_framework': { bg: 'warning', label: 'Decision Framework' },
      'workflow': { bg: 'info', label: 'Workflow' }
    };
    const v = variants[type] || { bg: 'secondary', label: type };
    return <Badge bg={v.bg} text={v.bg === 'warning' ? 'dark' : undefined}>{v.label}</Badge>;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading Process Builder...</p>
      </Container>
    );
  }

  // DETAIL VIEW
  if (view === 'detail' && detailProcess) {
    return (
      <Container fluid className="py-4 px-4">
        <Button variant="link" className="mb-3 ps-0" onClick={() => setView('list')}>
          &larr; Back to Processes
        </Button>

        <Card className="shadow-sm">
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">{detailProcess.name}</h4>
              <div className="d-flex gap-2">
                {getProcessTypeBadge(detailProcess.process_type)}
                <Badge bg={detailProcess.status === 'published' ? 'success' : 'secondary'}>
                  {detailProcess.status}
                </Badge>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            {detailProcess.framework_name && (
              <p className="text-muted mb-3">
                <strong>Based on:</strong> {detailProcess.framework_name}
              </p>
            )}

            {detailProcess.description && (
              <div className="mb-4">
                <h6>Overview</h6>
                <p>{detailProcess.description}</p>
              </div>
            )}

            <h5 className="mb-3">Steps ({detailProcess.steps?.length || 0})</h5>

            {detailProcess.steps?.map((step, idx) => (
              <Card key={idx} className="mb-3">
                <Card.Body>
                  <div className="d-flex align-items-start">
                    <div className="me-3">
                      <Badge bg="dark" style={{ fontSize: '1rem', padding: '0.5em 0.75em' }}>
                        {step.step_number || idx + 1}
                      </Badge>
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="mb-2">{step.action}</h6>
                      {step.details && <p className="text-muted mb-2">{step.details}</p>}

                      {step.dependencies?.length > 0 && (
                        <p className="small mb-2">
                          <strong>Depends on:</strong> {step.dependencies.join(', ')}
                        </p>
                      )}

                      {step.decision_point?.has_decision && (
                        <Alert variant="info" className="py-2 mb-2">
                          <strong>Decision:</strong> {step.decision_point.question}
                          <ul className="mb-0 mt-1">
                            <li>If YES: {step.decision_point.if_yes}</li>
                            <li>If NO: {step.decision_point.if_no}</li>
                          </ul>
                        </Alert>
                      )}

                      {step.common_mistakes?.length > 0 && (
                        <div className="mb-2">
                          <strong className="text-danger small">Common Mistakes:</strong>
                          <ul className="small mb-0">
                            {step.common_mistakes.map((m, i) => (
                              <li key={i}>{m}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {step.done_right && (
                        <p className="small text-success mb-0">
                          <strong>Done Right:</strong> {step.done_right}
                        </p>
                      )}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))}

            {detailProcess.common_mistakes?.length > 0 && (
              <div className="mt-4">
                <h6 className="text-danger">Overall Common Mistakes</h6>
                <ul>
                  {detailProcess.common_mistakes.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              </div>
            )}

            {detailProcess.done_right_criteria?.length > 0 && (
              <div className="mt-4">
                <h6 className="text-success">Quality Checklist</h6>
                <ul>
                  {detailProcess.done_right_criteria.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="d-flex gap-2 mt-4">
              <Button variant="outline-primary" onClick={() => handleExport(detailProcess.id)}>
                Export as Markdown
              </Button>
              <Button
                variant="outline-danger"
                onClick={() => {
                  setDeleteId(detailProcess.id);
                  setShowDeleteModal(true);
                }}
              >
                Delete Process
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Delete Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Delete Process</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete this process? This action cannot be undone.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    );
  }

  // COMPLETE VIEW
  if (view === 'complete' && completedProcess) {
    return (
      <Container fluid className="py-4 px-4">
        <Card className="shadow">
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
            <h4 className="mb-0">Process Created!</h4>
          </Card.Header>
          <Card.Body>
            <Alert variant="success" className="mb-4">
              Your process "{completedProcess.name}" has been created with {completedProcess.steps?.length || 0} steps.
            </Alert>

            <h5 className="mb-3">{completedProcess.name}</h5>
            <p className="text-muted">{completedProcess.description}</p>

            <h6 className="mt-4 mb-3">Steps Preview:</h6>
            <ListGroup className="mb-4">
              {completedProcess.steps?.slice(0, 5).map((step, idx) => (
                <ListGroup.Item key={idx}>
                  <strong>Step {step.step_number}:</strong> {step.action}
                </ListGroup.Item>
              ))}
              {completedProcess.steps?.length > 5 && (
                <ListGroup.Item className="text-muted">
                  ...and {completedProcess.steps.length - 5} more steps
                </ListGroup.Item>
              )}
            </ListGroup>

            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant="dark"
                onClick={() => viewProcessDetail(completedProcess.id)}
                style={{ backgroundColor: colors.greyDarkest }}
              >
                View Full Process
              </Button>
              <Button variant="outline-primary" onClick={() => handleExport(completedProcess.id)}>
                Export as Markdown
              </Button>
              <Button variant="outline-secondary" onClick={() => {
                setCompletedProcess(null);
                setView('list');
              }}>
                Back to List
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // CHAT VIEW
  if (view === 'chat') {
    return (
      <Container fluid className="py-4 px-4">
        <Card className="shadow" style={{ height: 'calc(100vh - 150px)' }}>
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">Building Your Process</h5>
                <small>{getProcessTypeBadge(processType)}</small>
              </div>
              <Button variant="outline-light" size="sm" onClick={handleAbandon}>
                Abandon
              </Button>
            </div>
          </Card.Header>

          <Card.Body style={{ overflowY: 'auto', flex: 1, backgroundColor: '#f8f9fa' }}>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`mb-3 d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
              >
                <div
                  style={{
                    maxWidth: '80%',
                    padding: '12px 16px',
                    borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    backgroundColor: msg.role === 'user' ? colors.greyDarkest : 'white',
                    color: msg.role === 'user' ? 'white' : 'inherit',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {msg.content.replace('[PROCESS_COMPLETE]', '').trim()}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </Card.Body>

          {/* Finalize Section */}
          {readyToFinalize ? (
            <div className="p-3 border-top bg-light">
              <Alert variant="success" className="mb-3">
                Your process is ready! Give it a name and we'll generate the final document.
              </Alert>
              <Form.Group className="mb-3">
                <Form.Label>Process Name</Form.Label>
                <Form.Control
                  type="text"
                  value={processName}
                  onChange={(e) => setProcessName(e.target.value)}
                  placeholder="e.g., Client Onboarding Checklist"
                />
              </Form.Group>
              <div className="d-flex gap-2">
                <Button
                  variant="dark"
                  onClick={handleFinalize}
                  disabled={sending || !processName.trim()}
                  style={{ backgroundColor: colors.greyDarkest }}
                >
                  {sending ? <><Spinner size="sm" className="me-2" /> Generating...</> : 'Generate Process Document'}
                </Button>
                <Button variant="outline-secondary" onClick={() => setReadyToFinalize(false)}>
                  Continue Editing
                </Button>
              </div>
            </div>
          ) : (
            <Form onSubmit={handleSendMessage} className="p-3 border-top">
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  placeholder="Describe your process..."
                  disabled={sending}
                />
                <Button
                  type="submit"
                  variant="dark"
                  disabled={sending || !userInput.trim()}
                  style={{ backgroundColor: colors.greyDarkest }}
                >
                  {sending ? <Spinner size="sm" /> : 'Send'}
                </Button>
              </div>
            </Form>
          )}
        </Card>
      </Container>
    );
  }

  // SELECT VIEW
  if (view === 'select') {
    return (
      <Container fluid className="py-4 px-4">
        <Button variant="link" className="mb-3 ps-0" onClick={() => setView('list')}>
          &larr; Back to Processes
        </Button>

        <Card className="shadow">
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
            <h4 className="mb-0">Create New Process</h4>
          </Card.Header>
          <Card.Body>
            {frameworks.length === 0 && (
              <Alert variant="info" className="mb-4">
                <strong>Tip:</strong> Extract your IP first (Module 2: Mind to Method) to create processes that connect to your frameworks.
                <Button
                  variant="link"
                  className="p-0 ms-2"
                  onClick={() => navigate('/command-center/authority-engineer/extract')}
                >
                  Extract IP Now
                </Button>
              </Alert>
            )}

            <h5 className="mb-3">1. Select Process Type</h5>
            <Row className="mb-4">
              <Col md={4} className="mb-3">
                <Card
                  className={`h-100 cursor-pointer ${processType === 'checklist' ? 'border-primary' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setProcessType('checklist')}
                >
                  <Card.Body className="text-center">
                    <div style={{ fontSize: '2rem' }}>
                      {processType === 'checklist' ? '&#9745;' : '&#9744;'}
                    </div>
                    <h6>Checklist</h6>
                    <small className="text-muted">
                      Linear steps, always in the same order
                    </small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-3">
                <Card
                  className={`h-100 ${processType === 'decision_framework' ? 'border-primary' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setProcessType('decision_framework')}
                >
                  <Card.Body className="text-center">
                    <div style={{ fontSize: '2rem' }}>
                      {processType === 'decision_framework' ? '&#9745;' : '&#9744;'}
                    </div>
                    <h6>Decision Framework</h6>
                    <small className="text-muted">
                      Branches based on conditions
                    </small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-3">
                <Card
                  className={`h-100 ${processType === 'workflow' ? 'border-primary' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setProcessType('workflow')}
                >
                  <Card.Body className="text-center">
                    <div style={{ fontSize: '2rem' }}>
                      {processType === 'workflow' ? '&#9745;' : '&#9744;'}
                    </div>
                    <h6>Workflow</h6>
                    <small className="text-muted">
                      Multiple people, handoffs, approvals
                    </small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {frameworks.length > 0 && (
              <>
                <h5 className="mb-3">2. Build From (Optional)</h5>
                <Row className="mb-4">
                  <Col md={4} className="mb-2">
                    <Card
                      className={`h-100 ${!selectedFramework ? 'border-primary' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelectedFramework(null)}
                    >
                      <Card.Body className="text-center py-3">
                        <strong>New Process</strong>
                        <br />
                        <small className="text-muted">Start from scratch</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  {frameworks.map(fw => (
                    <Col md={4} key={fw.id} className="mb-2">
                      <Card
                        className={`h-100 ${selectedFramework?.id === fw.id ? 'border-primary' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedFramework(fw)}
                      >
                        <Card.Body className="text-center py-3">
                          <strong>{fw.name}</strong>
                          <br />
                          <small className="text-muted">From your IP</small>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </>
            )}

            <div className="d-flex gap-2 mt-4">
              <Button
                variant="dark"
                onClick={handleStartBuilder}
                disabled={sending}
                style={{ backgroundColor: colors.greyDarkest }}
              >
                {sending ? <><Spinner size="sm" className="me-2" /> Starting...</> : 'Start Building Process'}
              </Button>
              <Button variant="outline-secondary" onClick={() => setView('list')}>
                Cancel
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // LIST VIEW (default)
  return (
    <Container fluid className="py-4 px-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Process Builder</h1>
          <p className="text-muted mb-0">
            Turn your IP and expertise into executable SOPs, checklists, and workflows.
          </p>
        </div>
        <Button
          variant="dark"
          onClick={startNewProcess}
          style={{ backgroundColor: colors.greyDarkest }}
        >
          + New Process
        </Button>
      </div>

      {/* Empty State */}
      {processes.length === 0 ? (
        <Card className="shadow-sm text-center py-5">
          <Card.Body>
            <div style={{ fontSize: '4rem', opacity: 0.3 }}>&#128221;</div>
            <h4 className="mt-3">No Processes Yet</h4>
            <p className="text-muted mb-4">
              Document your first process to make your expertise transferable.
            </p>
            <Button
              variant="dark"
              onClick={startNewProcess}
              style={{ backgroundColor: colors.greyDarkest }}
            >
              Create Your First Process
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {processes.map(process => (
            <Col lg={6} key={process.id} className="mb-4">
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h5 className="mb-0">{process.name}</h5>
                    {getProcessTypeBadge(process.process_type)}
                  </div>

                  {process.description && (
                    <p className="text-muted small mb-2" style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {process.description}
                    </p>
                  )}

                  <div className="d-flex gap-3 small text-muted mb-3">
                    <span>{process.step_count} steps</span>
                    {process.framework_name && (
                      <span>From: {process.framework_name}</span>
                    )}
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-dark"
                      size="sm"
                      onClick={() => viewProcessDetail(process.id)}
                    >
                      View
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleExport(process.id)}
                    >
                      Export
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => {
                        setDeleteId(process.id);
                        setShowDeleteModal(true);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Info Card */}
      <Card className="mt-4 border-0" style={{ backgroundColor: '#fff8e6' }}>
        <Card.Body>
          <h6 className="mb-2" style={{ color: colors.goldDark }}>
            <span className="me-2">&#128161;</span>
            Why Document Your Processes?
          </h6>
          <p className="small text-muted mb-0">
            Documented processes enable delegation, ensure consistency, reduce training time,
            and increase your business value. A well-documented SOP turns tribal knowledge
            into a transferable business asset.
          </p>
        </Card.Body>
      </Card>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Process</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this process? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ProcessBuilder;
